import { supabase } from '../lib/supabase_client';
import type { Session } from '@supabase/supabase-js';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** True when the access token still has at least `minTtlMs` of life left. */
export function isSessionFresh(session: Session | null | undefined, minTtlMs = 60_000): boolean {
  return !!(session?.expires_at && session.expires_at * 1000 > Date.now() + minTtlMs);
}

/**
 * Serialize forced refreshes app-wide.
 *
 * Refresh tokens are one-time-use. Concurrent `refreshSession()` calls
 * (EditFilings save + supabase auto-refresh + multi-tab) can trip Supabase's
 * reuse detection and revoke the *entire* session — which looks like a random
 * logout. One in-flight refresh at a time avoids that.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Ensure we have a usable access token without racing auto-refresh.
 *
 * Order:
 *  1. Return immediately if JWT still has headroom
 *  2. Wait briefly for supabase-js / another tab to finish an in-flight refresh
 *  3. Only then force a single `refreshSession()`
 */
export async function ensureFreshSession(minTtlMs = 60_000): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (isSessionFresh(session, minTtlMs)) return true;

      // Near-expiry or missing — give auto-refresh / other tabs a moment first.
      // Supabase's refresh-token reuse interval is ~10s; short waits catch races
      // without making Save feel sluggish.
      for (const wait of session ? [500, 1000] : [300, 1000, 2000]) {
        await sleep(wait);
        const { data: { session: again } } = await supabase.auth.getSession();
        // After waiting, accept even a short remaining TTL — token is usable now.
        if (isSessionFresh(again, 10_000)) return true;
        if (again?.access_token && again.expires_at && again.expires_at * 1000 > Date.now()) {
          return true;
        }
      }

      const { data, error } = await supabase.auth.refreshSession();
      return !error && !!data.session;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * After an unexpected SIGNED_OUT, wait for a sibling tab / in-flight refresh
 * to put a valid session back into storage before treating it as a real logout.
 */
export async function recoverAfterSignedOut(): Promise<Session | null> {
  for (const wait of [300, 1000, 2000]) {
    await sleep(wait);
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return session;
  }
  return null;
}
